import { FeatureSet, isFeatureEnabled } from "@picovico/utils";
import { FeatureKey } from "custom-types";
import { useMemo } from "react";
import { useAppSelector } from "store/hooks";

/**
 * Modules whose keys are OFF unless the set says `true`, the exception to the
 * fail-open rule below (claude/design/whatsapp-messaging/99 D14, 02 §11.5).
 * Every `whatsapp*` key is off when it is absent: an amphitheater from before
 * WhatsApp sends no `whatsapp` keys, and failing open there would offer
 * "Send my ticket on WhatsApp" and a WhatsApp code to every company before
 * the feature exists. The same rule as pvapp (`FeatureKeys.defaultOff`),
 * dolby and edm_react.
 */
export const DEFAULT_OFF_FEATURE_MODULES: readonly string[] = ["whatsapp"];

/** Whether `key` belongs to a module of `DEFAULT_OFF_FEATURE_MODULES`. */
export function isDefaultOffFeature(key: string): boolean {
  return DEFAULT_OFF_FEATURE_MODULES.some(
    (module) => key === module || key.startsWith(`${module}.`),
  );
}

/**
 * The web's rule for one key. Fail-open (`isFeatureEnabled`) for every key
 * except the default-off modules, where the key and each of its ancestors must
 * be explicitly `true`: no set, a missing key or a missing parent all read as
 * off.
 */
export function isWebFeatureEnabled(
  features: FeatureSet | null | undefined,
  key: string,
): boolean {
  if (!isDefaultOffFeature(key)) return isFeatureEnabled(features, key);
  if (!features) return false;
  const parts = key.split(".");
  for (let depth = 1; depth <= parts.length; depth += 1) {
    if (features[parts.slice(0, depth).join(".")] !== true) return false;
  }
  return true;
}

/**
 * Whether the company has `key` switched on, from the `features` of the web
 * config (`/api/web-fe/config/`). Fail-open: an old backend (no `features`),
 * a config that has not loaded yet and a key the set does not mention all read
 * as on, so nothing flashes hidden. A sub-feature is off whenever its module
 * is. Exception: `whatsapp*` keys are off unless explicitly on
 * (`isWebFeatureEnabled`). The server enforces; this only hides what the
 * company does not have.
 */
export function useWebFeature(key: FeatureKey): boolean {
  const features = useAppSelector((state) => state.config.features);
  return isWebFeatureEnabled(features, key);
}

/** The social sign-in provider that `google_signin` switches. */
const GOOGLE_PROVIDER = "google";

/**
 * The configured social providers minus those the company has not got: Google
 * is dropped while `google_signin` is off. Other providers are
 * not feature-flagged and pass through. `undefined` stays `undefined`.
 */
export function useVisibleSocialProviders<T extends { providerName: string }>(
  providers: T[] | undefined,
): T[] | undefined {
  const googleEnabled = useWebFeature("google_signin");
  return useMemo(
    () =>
      googleEnabled
        ? providers
        : providers?.filter(
            (provider) =>
              provider.providerName.toLowerCase() !== GOOGLE_PROVIDER,
          ),
    [providers, googleEnabled],
  );
}
